import formatLocale from "@atlaskit/prs-locale-mapper";
import { ConfigRegistryDataSource, Versions } from "@atlassiansox/flight-deck-core";
import { CdnDataSource } from "./clients/cdn/cdn-data-source";
import { CdnHttpClientImpl } from "./clients/cdn/cdn-http-client";
import { ConfigClient } from "./clients/config-client";
import { ConfigRegistryHttpClientImpl } from "./clients/config-registry/config-registry-http-client-impl";
import { getFlightDeckFetchExperience, getVersionMetadataFetchExperience } from "./metrics";
import { assertUnreachable } from "./utils/unreachable";
const DefaultNamespace = "flight-deck";
export { parseConfigWithTargeting, TargetedConfig } from "@atlassiansox/flight-deck-targeted-config";

/**
 * Micros environment type (environment minus region) with an option for a custom config registry namespace for non-production environments.
 * note: if you think you want `dev`, you probably want `ddev`.
 * See https://hello.atlassian.net/wiki/spaces/MICROS/pages/167212857/Environment%2Bregions%2Band%2Domains
 */

/**
 * Arguments for passing in dimensions that the artifact might vary on.
 * For more details see: https://developer.atlassian.com/platform/flight-deck/getting-your-capability-onto-flight-deck/dimensions/
 */

/**
 * @param artifactName The name of the Flight Deck artifact to fetch. The artifact must be declared as a frontend artifact in its Flight Deck
artifact definition; if it is not, this function with throw an error (i.e. return a rejected promise)
 * @param dim Arguments for passing in dimensions that the artifact might vary on.
For more details see: https://developer.atlassian.com/platform/flight-deck/getting-your-capability-onto-flight-deck/dimensions/
 * @returns artifact
 */
export async function fetchFlightDeckConfiguration(artifactName) {
  let dim = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const environment = getEnvFromEnvDependentDimensions(dim.environmentType);
  const locale = dim.locale && formatLocale(dim.locale);
  const artifactDimensions = {
    environment,
    locale
  };
  const client = await getConfigClient(dim);
  return client.getArtifact(artifactName, artifactDimensions);
}

/**
 * Gets latest version metadata
 * @param dim Micros environment type (environment minus region) with an option for a custom config registry namespace for non-production environments.
 * @returns latest version metadata
 */
export async function getLatestVersionMetadata() {
  let dim = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const client = await getConfigClient(dim);
  return client.getLatestVersionMetadata();
}
async function getConfigClient(dim) {
  /**
   * We fetch from the CDN when production config is requested, but outside of production, we fetch
   * the config from elsewhere: this allows us to avoid exposing config data from non-prod environments
   * to production. If an environment is not specified then we default to reading from the CDN like in
   * production.
   */
  const fetchArtifactExperience = await getFlightDeckFetchExperience();
  const fetchVersionMetadataExperience = await getVersionMetadataFetchExperience();
  if (dim.environmentType === undefined || dim.environmentType === "prod") {
    return new ConfigClient(new CdnDataSource(new CdnHttpClientImpl(), Versions.ManifestVersion), "CDN", fetchArtifactExperience, fetchVersionMetadataExperience);
  } else if (dim.environmentType === "adev" || dim.environmentType === "pdev" || dim.environmentType === "ddev" || dim.environmentType === "stg") {
    var _dim$namespace;
    /**
     * For other environments, a namepace must be specified.
     * In this case,we fetch directly from the config registry with the specified namespace (for testing purposes).
     */

    return new ConfigClient(new ConfigRegistryDataSource(Versions.ManifestVersion, new ConfigRegistryHttpClientImpl((_dim$namespace = dim.namespace) !== null && _dim$namespace !== void 0 ? _dim$namespace : DefaultNamespace, "flight-deck-front-end-sdk")), "Config Registry", fetchArtifactExperience, fetchVersionMetadataExperience);
  }
  assertUnreachable(dim.environmentType);
}

/**
 *
 * This function is necessary since all artifacts are tagged using an environment,
 * so to get an artifact we need to specify an environment. However, users only
 * specify an environmentType since that's all that matters to them. Flight Deck
 * ensures that frontend artifacts don't vary based on region so this is safe to do.
 *
 * @param environmentDependentDimensions
 * @returns a specific environment that has that envtype
 */
function getEnvFromEnvDependentDimensions(environmentDependentDimensions) {
  if (environmentDependentDimensions === undefined || environmentDependentDimensions === "prod") {
    return "prod-west";
  }
  switch (environmentDependentDimensions) {
    case "stg":
      return "stg-west";
    case "adev":
      return "adev";
    case "ddev":
      return "ddev";
    case "pdev":
      return "pdev-apse2";
  }
}